import type { UserClaim, UserPolicy } from '@/types';
import { fetchUserPolicyClaims, filterSortPolicyClaims } from '@/utils/claims';
import { getIsCancelledError } from '@/utils/error';
import { useShallow } from 'zustand/react/shallow';
import { createWithDevtools } from './utils';

interface ClaimsStateProps {
  error: Error | null;
  hasError: boolean;
  isLoading: boolean;
  lastUpdated: number;
  data: {
    past: UserClaim[];
    active: UserClaim[];
    cancelled: UserClaim[];
  };
}

interface ClaimsStateActions {
  resetClaimsStatus: () => void;
  resetClaimsData: () => void;
  fetchAllClaims: (policies: UserPolicy[]) => Promise<void>;
}

type ClaimsState = ClaimsStateProps & ClaimsStateActions;

const initialStateProps: ClaimsStateProps = {
  error: null,
  hasError: false,
  isLoading: true,
  lastUpdated: Date.now(),
  data: {
    past: [],
    active: [],
    cancelled: [],
  },
};

const useStore = createWithDevtools<ClaimsState>(
  set => ({
    ...initialStateProps,
    resetClaimsStatus: () => {
      const { error, hasError, isLoading } = initialStateProps;
      set({ error, hasError, isLoading });
    },
    resetClaimsData: () => {
      const { data } = initialStateProps;
      set({ lastUpdated: Date.now(), data });
    },
    fetchAllClaims: async (policies: UserPolicy[]): Promise<void> => {
      set({ error: null, hasError: false, isLoading: true });
      const { data = {}, error } = await fetchUserPolicyClaims();

      // Ignore previous/pending requests that are cancelled.
      if (getIsCancelledError(error)) {
        return;
      }

      if (error) {
        set({
          data: { ...initialStateProps.data },
          lastUpdated: Date.now(),
          isLoading: false,
          hasError: true,
          error,
        });
        return;
      }

      const { past, active, cancelled } = filterSortPolicyClaims(data, policies);

      set({
        data: { past, active, cancelled },
        lastUpdated: Date.now(),
        isLoading: false,
      });
    },
  }),
  'ClaimsStore',
);

// Memoized version of the store, using 'selector' with 'useShallow()'.
const useClaimsStore = <T>(selector: (state: ClaimsState) => T) => {
  return useStore(useShallow(selector));
};

const selectClaimsActions = (state: ClaimsState) => {
  return {
    resetClaimsStatus: state.resetClaimsStatus,
    resetClaimsData: state.resetClaimsData,
    fetchAllClaims: state.fetchAllClaims,
  };
};

const selectPastClaims = (state: ClaimsState) => {
  return { pastClaims: state.data.past, pastUpdated: state.lastUpdated };
};

const selectActiveClaims = (state: ClaimsState) => {
  return { activeClaims: state.data.active, activeUpdated: state.lastUpdated };
};

const selectCancelledClaims = (state: ClaimsState) => {
  return { cancelledClaims: state.data.cancelled, cancelledUpdated: state.lastUpdated };
};

const selectClaimsStatus = (state: ClaimsState) => {
  return { error: state.error, hasError: state.hasError, isLoading: state.isLoading };
};

const selectRecentActiveClaim = (state: ClaimsState) => {
  return state.data.active?.[0] ?? null;
};

// Only to be used within test files.
export const claimsStore = useStore;

export {
  initialStateProps,
  selectActiveClaims,
  selectCancelledClaims,
  selectClaimsActions,
  selectClaimsStatus,
  selectPastClaims,
  selectRecentActiveClaim,
  useClaimsStore,
};

export type { ClaimsState, ClaimsStateActions, ClaimsStateProps };
